import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { Search, Users, Store, MessageCircle, Plus, Sparkles, MapPin, Trophy, Eye, BookOpen, ArrowRight, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import VerifiedBadge from "@/components/VerifiedBadge";
import SocialIcons from "@/components/SocialIcons";
import ContributorBadge from "@/components/ContributorBadge";

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "businesses" ? "businesses" : "members");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [kec, setKec] = useState("all");
  const [kecCounts, setKecCounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [onlyPengurus, setOnlyPengurus] = useState(false);

  const switchTab = (next) => {
    setTab(next);
    setQ("");
    setSearchParams(next === "businesses" ? { tab: "businesses" } : {});
  };

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/featured/business"); setFeatured(data); }
    catch (err) { console.error("Failed to load featured:", err); }
    try { const { data } = await api.get("/categories"); setCategories(data); }
    catch (err) { console.error("Failed to load categories:", err); }
    try { const { data } = await api.get("/members/kecamatan-counts"); setKecCounts(data); }
    catch (err) { console.error("Failed to load kecamatan counts:", err); }
    try { const { data } = await api.get("/leaderboard/monthly"); setLeaderboard(data); }
    catch (err) { console.error("Failed to load leaderboard:", err); }
  })(); }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab, cat, kec, onlyPengurus]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const load = async () => {
    if (tab === "members") {
      try {
        const params = {};
        if (q) params.q = q;
        if (kec && kec !== "all") params.kecamatan = kec;
        if (onlyPengurus) params.role = "pengurus";
        const { data } = await api.get("/members", { params });
        setMembers(data);
      } catch (err) { console.error("Failed to load members:", err); }
    } else {
      try {
        const { data } = await api.get("/businesses", { params: { q: q || undefined, category: cat } });
        setBusinesses(data);
      } catch (err) { console.error("Failed to load businesses:", err); }
    }
  };

  return (
    <Layout>
      <div className="mb-8" data-testid="dashboard-header">
        <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">Halo, {user?.name?.split(" ")[0]}</div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold mt-1">Direktori Anggota & Bisnis</h1>
        <p className="text-bizma-subtext mt-2">Jelajahi anggota dan bisnis komunitas kita.</p>
      </div>

      {featured && (
        <Link to={`/app/businesses/${featured.business_id}`} className="felt-card overflow-hidden mb-8 grid md:grid-cols-5 group transition-transform hover:-translate-y-1 hover:shadow-md" data-testid="featured-business-card">
          <div className="md:col-span-2 aspect-video md:aspect-auto bg-bizma-muted">
            {featured.photo_path && <img src={fileUrl(featured.photo_path)} alt={featured.name} className="w-full h-full object-cover" />}
          </div>
          <div className="p-6 md:col-span-3 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-bizma-terracotta font-bold">
              <Sparkles size={14}/> Bisnis Pilihan Minggu Ini
            </div>
            <div className="mt-2 text-2xl font-display font-bold group-hover:text-bizma-terracotta transition-colors">{featured.name}</div>
            <div className="text-sm text-bizma-sage font-semibold mt-1">{featured.category}</div>
            <p className="mt-3 text-[#4A4743] line-clamp-2">{featured.description}</p>
            <div className="mt-3 text-sm text-bizma-subtext">oleh <span className="text-bizma-ink font-medium">{featured.owner?.name}</span></div>
          </div>
        </Link>
      )}

      {leaderboard && (leaderboard.top_writers?.length > 0 || leaderboard.top_viewed?.length > 0) && (
        <div className="mb-8" data-testid="leaderboard-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-bizma-terracotta font-bold flex items-center gap-1"><Trophy size={12}/> Papan Peringkat</div>
              <h2 className="font-display font-semibold text-lg sm:text-xl mt-1">Top Kontributor Blog</h2>
              <div className="text-sm text-bizma-subtext">{leaderboard.month}</div>
            </div>
            <Link to="/app/blog" className="text-sm text-bizma-terracotta font-medium inline-flex items-center gap-1 hover:underline self-start sm:self-auto" data-testid="leaderboard-see-all">Lihat blog <ArrowRight size={14}/></Link>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <LeaderCard title="Terbanyak Menulis" icon={<BookOpen size={14}/>} items={leaderboard.top_writers} metricLabel="artikel" metricKey="count" testId="leader-writers" />
            <LeaderCard title="Terbanyak Dilihat" icon={<Eye size={14}/>} items={leaderboard.top_viewed} metricLabel="views" metricKey="views" testId="leader-viewed" />
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-6" data-testid="dashboard-tabs">
        <TabBtn active={tab === "members"} onClick={() => switchTab("members")} icon={<Users size={16}/>} testId="tab-members">Anggota</TabBtn>
        <TabBtn active={tab === "businesses"} onClick={() => switchTab("businesses")} icon={<Store size={16}/>} testId="tab-businesses">Bisnis</TabBtn>
        <div className="ml-auto hidden sm:block">
          <Link to="/app/businesses/new" className="btn-primary text-sm py-2 px-4" data-testid="add-business-cta">
            <Plus size={16}/> Tambah Bisnis
          </Link>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-bizma-subtext" size={18} />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={tab === "members" ? "Cari anggota..." : "Cari bisnis..."}
            className="input-bizma pl-11" data-testid="search-input" />
        </div>
        {tab === "businesses" && (
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="input-bizma sm:w-56" data-testid="category-filter">
            <option value="all">Semua Kategori</option>
            {categories.map(c => <option key={c.category_id} value={c.name}>{c.name} ({c.count ?? 0})</option>)}
          </select>
        )}
        {tab === "members" && (
          <select value={kec} onChange={(e) => setKec(e.target.value)} className="input-bizma sm:w-64" data-testid="kecamatan-filter">
            <option value="all">Semua Kecamatan</option>
            {kecCounts.map(k => <option key={k.kecamatan} value={k.kecamatan}>{k.kecamatan} ({k.count})</option>)}
          </select>
        )}
      </div>

      {tab === "members" && kecCounts.length > 0 && (
        <div className="mb-6 space-y-3" data-testid="member-filter-bar">
          <div className="flex flex-wrap items-center gap-2" data-testid="role-filter-chips">
            <button
              onClick={() => setOnlyPengurus(false)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${!onlyPengurus ? "bg-bizma-ink text-white" : "bg-white border border-bizma-border text-bizma-ink hover:bg-bizma-muted"}`}
              data-testid="chip-all-members">
              <Users size={12}/> Semua Anggota
            </button>
            <button
              onClick={() => setOnlyPengurus(true)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${onlyPengurus ? "bg-bizma-terracotta text-white" : "bg-white border border-bizma-border text-bizma-terracotta hover:bg-bizma-terracotta/10"}`}
              data-testid="chip-pengurus-only">
              <Shield size={12}/> Pengurus saja
            </button>
          </div>
          <div data-testid="kecamatan-chips">
            <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold mb-2">Grup per Kecamatan</div>
            <div className="flex flex-wrap gap-2">
              {kecCounts.map(k => (
                <Link key={k.kecamatan} to={`/app/kecamatan/${encodeURIComponent(k.kecamatan)}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-bizma-border text-sm hover:bg-bizma-muted hover:border-bizma-sage transition-colors"
                  data-testid={`kec-chip-${k.kecamatan}`}>
                  <MapPin size={12} className="text-bizma-sage"/> {k.kecamatan}
                  <span className="text-xs text-bizma-subtext">({k.count})</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "members" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" data-testid="members-grid">
          {members.map((m) => <MemberCard key={m.user_id} m={m} />)}
          {members.length === 0 && <EmptyState msg="Belum ada anggota ditemukan." />}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="businesses-grid">
          {businesses.map((b) => <BusinessCard key={b.business_id} b={b} />)}
          {businesses.length === 0 && <EmptyState msg="Belum ada bisnis ditemukan." />}
        </div>
      )}
    </Layout>
  );
}

function TabBtn({ active, onClick, icon, children, testId }) {
  return (
    <button onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-bizma-ink text-white" : "bg-white border border-bizma-border text-bizma-ink hover:bg-bizma-muted"}`}
      data-testid={testId}>
      {icon}{children}
    </button>
  );
}

function MemberCard({ m }) {
  const nav = () => { window.location.href = `/app/members/${m.username || m.user_id}`; };
  return (
    <div className="felt-card overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-md flex flex-col cursor-pointer" data-testid={`member-card-${m.user_id}`}>
      <div onClick={nav} className="p-6 flex flex-col items-center text-center flex-1">
        {m.photo_path ? (
          <img src={fileUrl(m.photo_path)} alt={m.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-bizma-sage/40" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-bizma-sage text-white grid place-items-center text-3xl font-semibold">{m.name?.charAt(0)}</div>
        )}
        <div className="mt-4 font-display font-semibold text-lg flex items-center gap-1 justify-center">
          {m.name}
          {m.is_verified && <VerifiedBadge size={16} />}
        </div>
        {m.username && <div className="text-xs text-bizma-subtext">@{m.username}</div>}
        <div className="mt-1 inline-flex items-center gap-1 text-sm text-bizma-sage font-medium"><MapPin size={12}/> {m.kecamatan || "-"}</div>
        <ContributorBadge active={m.is_top_contributor} className="mt-2" />
        {m.first_business ? (
          <button onClick={(e)=>{e.stopPropagation(); window.location.href = `/app/businesses/${m.first_business.business_id}`;}}
            className="mt-2 text-xs text-bizma-terracotta hover:underline" data-testid={`member-owner-of-${m.user_id}`}>
            Owner {m.first_business.name}
          </button>
        ) : (
          <div className="mt-2 text-xs text-bizma-subtext">Belum ada bisnis</div>
        )}
        <SocialIcons socials={m.socials} className="mt-3 justify-center" />
      </div>
      {m.whatsapp && (
        <div className="px-6 pb-6">
          <button
            onClick={(e) => { e.stopPropagation(); window.open(`https://wa.me/${m.whatsapp.replace(/\D/g, "")}`, "_blank", "noopener,noreferrer"); }}
            className="btn-wa text-sm py-2 px-4 w-full" data-testid={`whatsapp-btn-${m.user_id}`}>
            <MessageCircle size={16} /> WhatsApp
          </button>
        </div>
      )}
    </div>
  );
}

function BusinessCard({ b }) {
  return (
    <Link to={`/app/businesses/${b.business_id}`} className="felt-card overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-md" data-testid={`business-card-${b.business_id}`}>
      <div className="aspect-video bg-bizma-muted overflow-hidden">
        {b.photo_path ? (
          <img src={fileUrl(b.photo_path)} alt={b.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-bizma-subtext"><Store /></div>
        )}
      </div>
      <div className="p-5">
        <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">{b.category}</div>
        <div className="font-display font-semibold text-lg mt-1">{b.name}</div>
        <div className="text-sm text-[#4A4743] line-clamp-2 mt-1">{b.description}</div>
        <div className="mt-3 flex items-center justify-between text-sm text-bizma-subtext">
          <span>oleh <span className="font-medium text-bizma-ink">{b.owner_name}</span></span>
        </div>
      </div>
    </Link>
  );
}

function EmptyState({ msg }) {
  return <div className="col-span-full felt-card p-10 text-center text-bizma-subtext" data-testid="empty-state">{msg}</div>;
}

function LeaderCard({ title, icon, items, metricLabel, metricKey, testId }) {
  const medals = ["bg-[#F5C543] text-white", "bg-[#C0C0C0] text-white", "bg-[#CD7F32] text-white"];
  return (
    <div className="felt-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-bizma-sage font-bold">{icon} {title}</div>
      <div className="mt-3 space-y-2">
        {(items || []).length === 0 && <div className="text-sm text-bizma-subtext py-2" data-testid={`${testId}-empty`}>Belum ada kontributor bulan ini.</div>}
        {(items || []).map((u, i) => (
          <Link key={u.user_id} to={`/app/members/${u.username || u.user_id}`} className="flex items-center gap-3 rounded-2xl px-2 py-2 hover:bg-bizma-muted transition-colors" data-testid={`${testId}-item-${i}`}>
            <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${medals[i] || "bg-bizma-muted text-bizma-subtext"}`}>{i+1}</div>
            {u.photo_path ? <img src={fileUrl(u.photo_path)} alt="" className="w-9 h-9 rounded-full object-cover"/> :
              <div className="w-9 h-9 rounded-full bg-bizma-sage text-white grid place-items-center text-sm font-semibold">{u.name?.charAt(0)}</div>}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate flex items-center gap-1">{u.name}{u.is_verified && <VerifiedBadge size={12}/>}</div>
              {u.username && <div className="text-xs text-bizma-subtext truncate">@{u.username}</div>}
            </div>
            <div className="text-right shrink-0">
              <div className="font-display font-bold text-lg leading-none">{u[metricKey] || 0}</div>
              <div className="text-[10px] text-bizma-subtext uppercase">{metricLabel}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
